// Contract between the web app and the API server (server/routes.ts implements it under /api).
import type { AuditEvent, ChatMessage, LevelId, MasterData, Notification, Order, Priority, PurchasedResource, Role, User } from "@/domain/types";
import type { SyncIssue } from "@/domain/registry";
import type { MasterChange } from "@/domain/masterDiff";

export interface MasterVersion {
  version: number;
  at: string;
  byName: string;
  changes: MasterChange[];
}

export interface Me extends User {
  isDev: boolean;
}

export interface PositionInput {
  role: Role;
  teamId: string | null;
  namecode: string | null;
}

/** One sign-up position waiting for a Leader. `key` identifies it in approve / reject calls. */
export interface PendingRequest {
  key: string;
  email: string;
  fullName: string;
  employeeCode: string;
  role: Role | null;
  teamId: string | null;
  namecode: string | null;
  /** The person already works here; this adds a position. */
  newPosition: boolean;
  createdAt: string;
}

export interface ApprovalChannels {
  email: boolean;
  telegram: boolean;
  /** PUBLIC_URL is set, so emails / Telegram messages can carry Approve / Reject links. */
  links: boolean;
}

export interface AuthConfig {
  googleClientId: string;
  /** Email-only sign-in, available only on non-production servers. */
  devLogin: boolean;
  /** Email + password sign-in for the built-in developer/Leader account. */
  passwordLogin: boolean;
  registerFormUrl: string;
}

export interface DirectoryEntry {
  id: string;
  fullName: string;
  /** Main position; `roles` lists every position the person holds. */
  role: Role | null;
  roles?: Role[];
  teamId: string | null;
  teamIds?: string[];
  namecode: string | null;
  namecodes?: string[];
  /** Which Namecode belongs to which editor team. */
  editorTeams?: { namecode: string; teamId: string | null }[];
  avatarUrl: string | null;
}

export type { SyncIssue } from "@/domain/registry";

export interface SyncStatus {
  at: string | null;
  ok: boolean;
  users: number;
  error: string | null;
  issues: SyncIssue[];
}

export interface CreateOrderInput {
  namecode: string;
  codeName: string;
  effectCount: number;
  requestedTypes: string[];
  inputUrl: string;
  sampleUrl: string;
  instruction: string;
  priority: Priority;
  /** ISO start of the deadline clock; defaults to now. */
  startAt?: string;
}

export interface ClassifyInput {
  typeCode: string;
  levelId: LevelId;
}

export interface SubmitInput {
  /** Output link of a single-effect code; ignored when the code shares a folder. */
  outputUrl: string;
  prompt: string;
  toolId: string;
  credits: number;
  /** Link to the image output (required). */
  imageUrl: string;
  /** Shared output folder of a code with 2+ effects (required there); kept on the order for the next effects. */
  folderUrl: string;
  /** Dropbox / Drive, and the mail account that owns the link (required). */
  linkType: string;
  mail: string;
  /** A specific QC, or null for any QC. */
  qcId?: string | null;
}

export interface QcInput {
  decision: "PASS" | "RETURN";
  reason: string | null;
  checked: number;
}

export interface ChatThread {
  orderCode: string;
  codeName: string;
  lastMessage: ChatMessage | null;
  unread: number;
}

export interface Attachment {
  id: string;
  name: string;
  mime: string;
  size: number;
}

/** Accepted upload types: images, PDF, plain text documents and Office files. Never HTML/SVG. */
export const UPLOAD_TYPES: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "application/pdf": "pdf",
  "text/plain": "txt",
  "text/markdown": "md",
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
};
export const MAX_UPLOAD_MB = 10;
export const MAX_ATTACHMENTS = 6;

export interface DirectMessage {
  id: string;
  senderId: string;
  recipientId: string;
  text: string;
  at: string;
  attachments: Attachment[];
}

export interface DmThread {
  peerId: string;
  lastMessage: DirectMessage | null;
  unread: number;
}

/** userId → ISO time the user was last active in the app. */
export type Presence = Record<string, string>;

/** Seen within this window counts as online. */
export const ONLINE_WINDOW_MS = 75_000;

export const FEEDBACK_KINDS = ["Change a form", "Show / hide information", "Wording", "Something is broken", "Other"] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];
export type FeedbackStatus = "OPEN" | "IN_PROGRESS" | "DONE" | "WONT_FIX";

export interface FeedbackInput {
  route: string;
  screen: string;
  kind: FeedbackKind;
  message: string;
  viewport: string;
  attachmentIds?: string[];
}

export interface Feedback extends Omit<FeedbackInput, "attachmentIds"> {
  attachments: Attachment[];
  id: string;
  at: string;
  userId: string;
  userEmail: string;
  userName: string;
  role: Role | null;
  status: FeedbackStatus;
  devNote: string;
}

export class ApiError extends Error {
  readonly code: "UNAUTHENTICATED" | "FORBIDDEN" | "NOT_FOUND" | "VALIDATION" | "CONFLICT" | "NOT_REGISTERED" | "SERVER";
  /** Extra data for the screen (e.g. the sign-up waiting for approval). */
  readonly detail?: unknown;
  constructor(code: ApiError["code"], message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.detail = detail;
  }
}

export interface Api {
  auth: {
    config(): Promise<AuthConfig>;
    google(credential: string): Promise<Me>;
    dev(email: string): Promise<Me>;
    password(email: string, password: string): Promise<Me>;
    logout(): Promise<void>;
    me(): Promise<Me | null>;
    /** Sign-up waiting or rejected: send it to the approvers again, or say the person is editing it in the form. */
    signupResend(email: string, ticket: string): Promise<{ ok: true }>;
    signupEdit(email: string, ticket: string): Promise<{ ok: true }>;
  };
  users: {
    list(): Promise<User[]>;
    directory(): Promise<DirectoryEntry[]>;
    syncStatus(): Promise<SyncStatus>;
    sync(): Promise<SyncStatus>;
    rename(email: string, names: { fullName: string; publicName: string | null }): Promise<User>;
  };
  registrations: {
    /** Which approval channels the server can use. */
    channels(): Promise<ApprovalChannels>;
    approve(email: string): Promise<unknown>;
    /** Developer tool: create / delete a fake sign-up to try the approval flow. */
    createTest(): Promise<User | null>;
    deleteTest(email: string): Promise<unknown>;
    reject(email: string): Promise<unknown>;
    /** Sign-up positions waiting for a Leader. */
    pending(): Promise<PendingRequest[]>;
  };
  team: {
    addPerson(input: PositionInput & { employeeCode: string; fullName: string; email: string; password?: string }): Promise<User | null>;
    /** Empty password = remove it (Google sign-in only). */
    setPassword(code: string, password: string | null): Promise<{ ok: true }>;
    addPosition(code: string, input: PositionInput): Promise<User | null>;
    changePosition(key: string, input: Omit<PositionInput, "role">): Promise<User | null>;
    removePosition(key: string): Promise<User | null>;
    removePerson(code: string): Promise<unknown>;
    setStatus(code: string, input: { disabled: boolean; note?: string }): Promise<User | null>;
    fixRow(rowId: string, input: PositionInput & { employeeCode: string; fullName: string }): Promise<SyncStatus>;
    ignoreRow(rowId: string): Promise<SyncStatus>;
    /** Whether Leader edits were copied into the sheet's "App edits" tab. */
    mirror(): Promise<{ at: string | null; ok: boolean; error: string | null }>;
  };
  master: {
    get(): Promise<MasterData>;
    update(next: MasterData): Promise<MasterData>;
    history(): Promise<MasterVersion[]>;
  };
  orders: {
    list(): Promise<Order[]>;
    get(code: string): Promise<Order>;
    create(input: CreateOrderInput): Promise<Order>;
    accept(code: string): Promise<Order>;
    reject(code: string, reason: string): Promise<Order>;
  };
  effects: {
    claim(orderCode: string, effectId: string, input: ClassifyInput): Promise<Order>;
    /** Take several effects of one order at once (all or nothing). */
    claimMany(orderCode: string, items: ({ effectId: string } & ClassifyInput)[]): Promise<Order>;
    assign(orderCode: string, effectId: string, staffId: string, input: ClassifyInput): Promise<Order>;
    submit(orderCode: string, effectId: string, input: SubmitInput): Promise<Order>;
    qcDecide(orderCode: string, effectId: string, input: QcInput): Promise<Order>;
    /** Pass several effects of one order at once (all or nothing). */
    qcPassMany(orderCode: string, items: { effectId: string; checked: number }[]): Promise<Order>;
  };
  chat: {
    threads(): Promise<ChatThread[]>;
    messages(orderCode: string): Promise<ChatMessage[]>;
    send(orderCode: string, text: string, attachmentIds?: string[]): Promise<ChatMessage>;
  };
  dm: {
    threads(): Promise<DmThread[]>;
    messages(peerId: string): Promise<DirectMessage[]>;
    send(peerId: string, text: string, attachmentIds?: string[]): Promise<DirectMessage>;
  };
  uploads: {
    upload(file: File): Promise<Attachment>;
    url(id: string): string;
  };
  presence: {
    list(): Promise<Presence>;
  };
  audit: {
    list(): Promise<AuditEvent[]>;
    recordDenied(path: string): Promise<void>;
  };
  notifications: {
    list(): Promise<Notification[]>;
    markAllRead(): Promise<void>;
  };
  resources: {
    list(): Promise<PurchasedResource[]>;
    save(id: string | null, input: Omit<PurchasedResource, "id" | "updatedAt">): Promise<PurchasedResource>;
    remove(id: string): Promise<{ ok: true }>;
  };
  feedback: {
    create(input: FeedbackInput): Promise<Feedback>;
    list(): Promise<Feedback[]>;
    update(id: string, patch: { status?: FeedbackStatus; devNote?: string }): Promise<Feedback>;
  };
}
